import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { 
  FiBell, FiCheck, FiX, FiCheckCircle, FiAlertTriangle,
  FiCheckSquare, FiFolder, FiClock, FiMessageSquare, FiUsers, FiUpload,
  FiStar, FiTrendingUp, FiSettings, FiLoader, FiMail
} from 'react-icons/fi';
import { toast } from 'sonner';
import {
  getPersonalNotifications,
  getPersonalUnreadCount,
  markPersonalNotificationAsRead,
  markAllPersonalNotificationsAsRead,
  deletePersonalNotification
} from '../../services/personalNotifications.service';
import invitationService from '../../services/invitationService';
import * as personalTeamService from '../../services/personalTeamService';
import { useNavigate } from 'react-router-dom';

function getChatNotificationUrl(notification) {
  const conversationId = notification?.metadata?.conversationId;
  if (!conversationId) return null;
  const isChatNotification =
    notification.type === 'chat_message' ||
    notification.type === 'chat_mention' ||
    notification.actionUrl?.includes('/messages');
  if (!isChatNotification) return null;
  const isOrg = notification.workspace === 'organization' || notification.actionUrl?.startsWith('/org/');
  return `${isOrg ? '/org/messages' : '/messages'}?conversationId=${conversationId}`;
}

const PersonalNotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread
  const [invitationActionLoading, setInvitationActionLoading] = useState(null); // notificationId being acted on
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getPersonalUnreadCount();
      setUnreadCount(response.data?.count || 0);
    } catch (error) {
      console.error('Error fetching personal unread count:', error);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = { 
        limit: 15,
        ...(filter === 'unread' && { isRead: false })
      };
      const response = await getPersonalNotifications(params);
      setNotifications(response.data?.notifications || []);
    } catch (error) {
      console.error('Error fetching personal notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Load unread count on mount - use socket events instead of polling for efficiency
  useEffect(() => {
    fetchUnreadCount();
    
    // Reduce polling to every 60 seconds since we have real-time socket updates
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load notifications when dropdown opens or filter changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, filter, fetchNotifications]);

  // Listen for global notification sync events (from project widget or other sources)
  useEffect(() => {
    const handleNotificationRead = (event) => {
      const { notificationId } = event.detail;
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const handleNotificationReadAll = () => {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date() })));
      setUnreadCount(0);
    };

    const handleNotificationDeleted = (event) => {
      const { notificationId } = event.detail;
      setNotifications(prev => {
        const deleted = prev.find(n => n._id === notificationId);
        if (deleted && !deleted.isRead) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => n._id !== notificationId);
      });
    };

    const handleNewNotification = (event) => {
      const { notification } = event.detail;
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n._id === notification._id)) return prev;
        return [notification, ...prev];
      });
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
    };

    window.addEventListener('notification:read', handleNotificationRead);
    window.addEventListener('notification:readAll', handleNotificationReadAll);
    window.addEventListener('notification:deleted', handleNotificationDeleted);
    window.addEventListener('notification:new', handleNewNotification);

    return () => {
      window.removeEventListener('notification:read', handleNotificationRead);
      window.removeEventListener('notification:readAll', handleNotificationReadAll);
      window.removeEventListener('notification:deleted', handleNotificationDeleted);
      window.removeEventListener('notification:new', handleNewNotification);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsidePortal = portalRef.current && !portalRef.current.contains(event.target);
      
      if (isOutsideButton && isOutsidePortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await markPersonalNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(notifications.map(n =>
        n._id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      // Dispatch global event so other components can sync
      window.dispatchEvent(new CustomEvent('notification:read', { 
        detail: { notificationId } 
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllPersonalNotificationsAsRead();
      
      // Update local state
      const notificationIds = notifications.filter(n => !n.isRead).map(n => n._id);
      setNotifications(notifications.map(n => ({ ...n, isRead: true, readAt: new Date() })));
      setUnreadCount(0);
      
      // Dispatch global event so other components can sync
      window.dispatchEvent(new CustomEvent('notification:readAll', { 
        detail: { notificationIds } 
      }));
      
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const deletedNotification = notifications.find(n => n._id === notificationId);
      
      await deletePersonalNotification(notificationId);
      
      // Update local state
      setNotifications(notifications.filter(n => n._id !== notificationId));
      
      // Update unread count if it was unread
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
      
      // Dispatch global event so other components can sync
      window.dispatchEvent(new CustomEvent('notification:deleted', { 
        detail: { notificationId } 
      }));
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      try {
        await markPersonalNotificationAsRead(notification._id);
        setNotifications(notifications.map(n =>
          n._id === notification._id ? { ...n, isRead: true } : n
        ));
        setUnreadCount(Math.max(0, unreadCount - 1));
        
        // Dispatch global event so other components can sync
        window.dispatchEvent(new CustomEvent('notification:read', { 
          detail: { notificationId: notification._id } 
        }));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
    
    const chatUrl = getChatNotificationUrl(notification);
    if (chatUrl || notification.actionUrl) {
      navigate(chatUrl || notification.actionUrl);
      setIsOpen(false);
    }
  };

  // Helper to check if a notification is an invitation type
  const isInvitationNotification = (type) => {
    return [
      'personal_project_invitation_received',
      'org_invitation_received',
      'personal_team_invitation_received'
    ].includes(type);
  };

  // Handle inline accept from notification
  const handleInvitationAccept = async (notification, e) => {
    e.stopPropagation();
    const token = notification.metadata?.invitationToken;
    if (!token) {
      // Fallback: navigate to actionUrl
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        setIsOpen(false);
      }
      return;
    }

    try {
      setInvitationActionLoading(notification._id);
      const type = notification.type;

      if (type === 'personal_project_invitation_received') {
        await invitationService.respondToInvitation(token, 'accept');
        toast.success('You\'ve joined the project!');
      } else if (type === 'org_invitation_received') {
        await invitationService.acceptOrganizationInvitation(token);
        toast.success('You\'ve joined the organization!');
      } else if (type === 'personal_team_invitation_received') {
        await personalTeamService.acceptInvitation(token);
        toast.success('You\'ve joined the team!');
      }

      // Mark notification as read and update status
      try {
        await markPersonalNotificationAsRead(notification._id);
      } catch (_) {}
      setNotifications(prev => prev.map(n => 
        n._id === notification._id 
          ? { ...n, isRead: true, metadata: { ...n.metadata, invitationStatus: 'accepted' } }
          : n
      ));
      if (!notification.isRead) setUnreadCount(prev => Math.max(0, prev - 1));

      window.dispatchEvent(new CustomEvent('invitation:accepted', {
        detail: { notification }
      }));
    } catch (err) {
      console.error('Error accepting invitation:', err);
      const errMsg = err?.response?.data?.message || err.message || '';
      if (errMsg.toLowerCase().includes('invalid') || errMsg.toLowerCase().includes('expired') || 
          errMsg.toLowerCase().includes('already') || errMsg.toLowerCase().includes('not found')) {
        toast.info('This invitation has already been responded to or has expired');
        try { await markPersonalNotificationAsRead(notification._id); } catch (_) {}
        // Update to show accepted status (since the invitation was already responded to)
        setNotifications(prev => prev.map(n => 
          n._id === notification._id 
            ? { ...n, isRead: true, metadata: { ...n.metadata, invitationStatus: 'accepted' } }
            : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        toast.error(errMsg || 'Failed to accept invitation');
      }
    } finally {
      setInvitationActionLoading(null);
    }
  };

  // Handle inline decline from notification
  const handleInvitationDecline = async (notification, e) => {
    e.stopPropagation();
    const token = notification.metadata?.invitationToken;
    if (!token) return;

    try {
      setInvitationActionLoading(notification._id);
      const type = notification.type;

      if (type === 'personal_project_invitation_received') {
        await invitationService.respondToInvitation(token, 'decline');
        toast.info('Invitation declined');
      } else if (type === 'personal_team_invitation_received') {
        await personalTeamService.declineInvitation(token);
        toast.info('Invitation declined');
      } else if (type === 'org_invitation_received') {
        // Org invitations don't have a decline endpoint, dismiss
        toast.info('Invitation dismissed');
      }

      // Mark notification as read and update status
      try {
        await markPersonalNotificationAsRead(notification._id);
      } catch (_) {}
      setNotifications(prev => prev.map(n => 
        n._id === notification._id 
          ? { ...n, isRead: true, metadata: { ...n.metadata, invitationStatus: 'declined' } }
          : n
      ));
      if (!notification.isRead) setUnreadCount(prev => Math.max(0, prev - 1));

      window.dispatchEvent(new CustomEvent('invitation:declined', {
        detail: { notification }
      }));
    } catch (err) {
      console.error('Error declining invitation:', err);
      const errMsg = err?.response?.data?.message || err.message || '';
      if (errMsg.toLowerCase().includes('invalid') || errMsg.toLowerCase().includes('expired') || 
          errMsg.toLowerCase().includes('already') || errMsg.toLowerCase().includes('not found')) {
        toast.info('This invitation has already been responded to or has expired');
        try { await markPersonalNotificationAsRead(notification._id); } catch (_) {}
        setNotifications(prev => prev.map(n => 
          n._id === notification._id 
            ? { ...n, isRead: true, metadata: { ...n.metadata, invitationStatus: 'declined' } }
            : n
        ));
      } else {
        toast.error(errMsg || 'Failed to decline invitation');
      }
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const getNotificationIcon = (type) => {
    // Task notifications
    if (type?.startsWith('personal_task') || type?.startsWith('task')) {
      if (type.includes('completed')) {
        return <FiCheckCircle className="h-4 w-4 text-green-500" />;
      }
      if (type.includes('overdue')) {
        return <FiAlertTriangle className="h-4 w-4 text-red-500" />;
      }
      if (type.includes('due_soon')) {
        return <FiClock className="h-4 w-4 text-yellow-500" />;
      }
      return <FiCheckSquare className="h-4 w-4 text-blue-500" />;
    }
    
    // Project notifications
    if (type?.startsWith('personal_project') || type?.startsWith('project')) {
      if (type.includes('completed')) {
        return <FiCheckCircle className="h-4 w-4 text-green-500" />;
      }
      if (type.includes('milestone')) {
        return <FiStar className="h-4 w-4 text-yellow-500" />;
      }
      if (type.includes('progress')) {
        return <FiTrendingUp className="h-4 w-4 text-blue-500" />;
      }
      if (type.includes('deadline')) {
        return <FiAlertTriangle className="h-4 w-4 text-orange-500" />;
      }
      return <FiFolder className="h-4 w-4 text-purple-500" />;
    }
    
    // Reminder notifications
    if (type?.startsWith('personal_reminder') || type?.startsWith('reminder')) {
      return <FiClock className="h-4 w-4 text-orange-500" />;
    }
    
    // Discussion/comment notifications
    if (type?.startsWith('personal_discussion') || type?.includes('comment') || type?.includes('mention')) {
      return <FiMessageSquare className="h-4 w-4 text-indigo-500" />;
    }
    
    // Collaboration notifications
    if (type?.startsWith('personal_collab') || type?.includes('invite') || type?.includes('shared')) {
      return <FiUsers className="h-4 w-4 text-teal-500" />;
    }
    
    // Activity notifications
    if (type?.includes('file') || type?.includes('upload')) {
      return <FiUpload className="h-4 w-4 text-gray-500" />;
    }
    
    // System notifications
    if (type?.startsWith('personal_system') || type?.includes('welcome') || type?.includes('settings')) {
      return <FiSettings className="h-4 w-4 text-gray-500" />;
    }
    
    // Default
    return <FiBell className="h-4 w-4 text-gray-500" />;
  };

  const getTypeLabel = (type) => {
    if (type?.includes('task')) return 'Task';
    if (type?.includes('project')) return 'Project';
    if (type?.includes('reminder')) return 'Reminder';
    if (type?.includes('invitation_received') || type?.includes('invite')) return 'Invitation';
    if (type?.includes('discussion') || type?.includes('comment')) return 'Discussion';
    if (type?.includes('collab') || type?.includes('shared')) return 'Collaboration';
    if (type?.includes('system')) return 'System';
    return 'Notification';
  };

  const getTypeBadgeColor = (type) => {
    if (type?.includes('invitation_received') || type?.includes('invite')) return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    if (type?.includes('task')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    if (type?.includes('project')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    if (type?.includes('reminder')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    if (type?.includes('discussion') || type?.includes('comment')) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
    if (type?.includes('collab') || type?.includes('shared')) return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  // Portal dropdown content
  const dropdownContent = isOpen && ReactDOM.createPortal(
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 z-[9998]" 
        onClick={() => setIsOpen(false)}
      />
      
      {/* Dropdown */}
      <div 
           ref={portalRef}
           className="fixed right-4 top-14 w-[380px] bg-white dark:bg-gray-800 rounded-xl shadow-2xl 
                    border border-gray-200 dark:border-gray-700 z-[9999] flex flex-col"
           style={{ maxHeight: 'calc(100vh - 80px)' }}>
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 
                           dark:hover:text-blue-300 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
            
            {/* Filter tabs */}
            <div className="flex gap-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  filter === 'all'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  filter === 'unread'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>{`.notification-scroll::-webkit-scrollbar { display: none; }`}</style>
            {loading ? (
              <div className="flex items-center justify-center p-6">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                <FiBell className="h-10 w-10 mb-2 opacity-40" />
                <p className="text-sm">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  You'll see updates about your tasks & projects here
                </p>
              </div>
            ) : (
              <div className="notification-scroll">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer 
                              transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-b-0
                              ${!notification.isRead ? 'bg-blue-50/50 dark:bg-blue-900/5' : ''}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getTypeBadgeColor(notification.type)}`}>
                                {getTypeLabel(notification.type)}
                              </span>
                              {!notification.isRead && (
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                              )}
                            </div>
                            <p className={`text-sm leading-tight ${
                              !notification.isRead
                                ? 'font-medium text-gray-900 dark:text-white'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {notification.title}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {!notification.isRead && (
                              <button
                                onClick={(e) => handleMarkAsRead(notification._id, e)}
                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 
                                         hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Mark as read"
                              >
                                <FiCheck className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(notification._id, e)}
                              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 
                                       hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title="Delete"
                            >
                              <FiX className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        
                        {notification.message && (
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {notification.message}
                          </p>
                        )}

                        {/* Inline Accept/Decline for invitation notifications */}
                        {isInvitationNotification(notification.type) && notification.metadata?.invitationToken && (
                          <div className="mt-1.5 flex items-center gap-2">
                            {notification.metadata?.invitationStatus === 'accepted' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md">
                                <FiCheck className="h-3 w-3" />
                                Accepted
                              </span>
                            ) : notification.metadata?.invitationStatus === 'declined' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md">
                                <FiX className="h-3 w-3" />
                                Declined
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => handleInvitationAccept(notification, e)}
                                  disabled={invitationActionLoading === notification._id}
                                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
                                >
                                  {invitationActionLoading === notification._id ? (
                                    <FiLoader className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <FiCheck className="h-3 w-3" />
                                  )}
                                  Accept
                                </button>
                                <button
                                  onClick={(e) => handleInvitationDecline(notification, e)}
                                  disabled={invitationActionLoading === notification._id}
                                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
                                >
                                  <FiX className="h-3 w-3" />
                                  Decline
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          {notification.metadata?.projectName && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[150px]">
                              • {notification.metadata.projectName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 space-y-1">
              {/* Show invitations link if there are invitation notifications */}
              {notifications.some(n => isInvitationNotification(n.type)) && (
                <button
                  onClick={() => {
                    navigate('/personal/invitations');
                    setIsOpen(false);
                  }}
                  className="w-full py-1.5 text-center text-xs text-teal-600 hover:text-teal-700 
                           dark:text-teal-400 dark:hover:text-teal-300 font-medium 
                           hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors flex items-center justify-center gap-1.5"
                >
                  <FiMail className="w-3.5 h-3.5" />
                  View all pending invitations
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/personal/notifications');
                  setIsOpen(false);
                }}
                className="w-full py-1.5 text-center text-xs text-blue-600 hover:text-blue-700 
                         dark:text-blue-400 dark:hover:text-blue-300 font-medium 
                         hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      </>,
    document.body
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 
                 rounded-lg transition-colors"
        title="Notifications"
      >
        <FiBell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 
                         text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {dropdownContent}
    </div>
  );
};

export default PersonalNotificationBell;
